import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  AppBar,
  Toolbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  "Certifications": "/Certifications",
  "Languages": "/Language",
  "Accomplishments": "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  "Software": "/SoftWare",
  "References": "/Refferences",
  "Volunteering": "/Volunteer",
  "Interests": "/Interest",
};

const InterestPage = () => {
  const [interests, setInterests] = useState([""]);
  const [savedInterests, setSavedInterests] = useState([]);
  const { sections } = useSections();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(stored);
  }, []);

  const handleChange = (index, e) => {
    const newInterests = [...interests];
    newInterests[index] = e.target.value;
    setInterests(newInterests);
  };

  const handleAddField = () => {
    setInterests([...interests, ""]);
  };

  const handleEdit = (index) => {
    const itemToEdit = savedInterests[index];
    setInterests([itemToEdit]);
    handleDelete(index, false);
  };

  const handleDelete = (index, updateStorage = true) => {
    const updated = savedInterests.filter((_, i) => i !== index);
    setSavedInterests(updated);
    if (updateStorage) localStorage.setItem("interests", JSON.stringify(updated));
  };

  const handleSaveAndNext = () => {
    // Save valid interests if any
    const validInterests = interests.filter((i) => i.trim() !== "");
    if (validInterests.length > 0) {
      const updated = [...savedInterests, ...validInterests];
      setSavedInterests(updated);
      localStorage.setItem("interests", JSON.stringify(updated));
      setInterests([""]);
    }

    // Always navigate to next section
    const currentSection = "Interests";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex !== -1 && currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], { state: { selectedTemplate, selectedTheme } });
    } else {
      navigate("/mainpage", { state: { selectedTemplate, selectedTheme } });
    }
  };

  const handleSkip = () => {
    const currentSection = "Interests";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex !== -1 && currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], { state: { selectedTemplate, selectedTheme } });
    } else {
      navigate("/mainpage", { state: { selectedTemplate, selectedTheme } });
    }
  };

  return (
    <Box display="flex" flexDirection="column" bgcolor="#EFF7FF" minHeight="100vh">
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Add Your Interests
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Container
        maxWidth="md"
        sx={{
          mt: { xs: 2, md: 4 },
          mb: 4,
          px: { xs: 2, sm: 3, md: 4 },
          mx: "auto",
          width: "100%",
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid #B3D9F7",
            boxShadow: "0 4px 20px rgba(0, 43, 91, 0.12)",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#002B5B",
              fontWeight: "bold",
              mb: 2,
              borderBottom: "2px solid #0288D1",
              pb: 1,
            }}
          >
            Your Interests
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {interests.map((interest, index) => (
              <Grid item xs={12} key={index}>
                <TextField
                  label={`Interest ${index + 1}`}
                  fullWidth
                  value={interest}
                  onChange={(e) => handleChange(index, e)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#B3D9F7" },
                      "&:hover fieldset": { borderColor: "#0288D1" },
                      "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
                  }}
                />
              </Grid>
            ))}
          </Grid>

          <Button
            variant="outlined"
            sx={{
              mt: 2,
              mr: 1,
              borderColor: "#0288D1",
              color: "#002B5B",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#E1F5FE", borderColor: "#002B5B" },
            }}
            onClick={handleAddField}
          >
            + Add Another Interest
          </Button>

          {/* Save & Next — always enabled */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="text"
              onClick={handleSkip}
              sx={{
                color: "#0288D1",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#E1F5FE" },
              }}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAndNext}
              sx={{
                px: 4,
                fontWeight: "bold",
                backgroundColor: "#002B5B",
                "&:hover": { backgroundColor: "#0288D1" },
                borderRadius: 2,
                boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
              }}
            >
              Save & Next
            </Button>
          </Box>
        </Paper>

        {/* Saved Interests */}
        {savedInterests.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "bold",
                color: "#002B5B",
                borderBottom: "2px solid #0288D1",
                pb: 1,
              }}
            >
              Saved Interests
            </Typography>
            <Grid container spacing={2}>
              {savedInterests.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    sx={{
                      p: 1,
                      boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                      border: "1px solid #B3D9F7",
                      borderLeft: "4px solid #002B5B",
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
                    }}
                  >
                    <CardContent
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <Typography sx={{ wordBreak: "break-word", flex: 1, color: "#0D4C8B" }}>
                        {item}
                      </Typography>
                      <Box sx={{ display: "flex", flexShrink: 0 }}>
                        <IconButton
                          onClick={() => handleEdit(index)}
                          sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(index)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default InterestPage;